import {createStartupAttempt, MICROPHONE_TIMEOUT_MS, CONNECTION_TIMEOUT_MS} from "./voiceStartup";
import {safeVoiceId, voiceConfigurationAllowed} from "./practiceSimulatorVoiceConfig";
import axios from "axios";
import {PUBLIC_SESSION_CONFIG, isPublicSessionError} from "./publicAccountSession";
import {createStudyBuddyRealtimeTransport} from "./StudyBuddyRealtimeTransport";
import {MAX_DETERMINISTIC_FINALIZATION_FAILURES, classifyStudyBuddyFinalizationError, correctiveFinalizationResult} from "./studyBuddyFinalizationError";
import {REPORT_UNAVAILABLE, validatedPracticeReport} from "./practiceSimulatorReports";
export {safeVoiceId, voiceConfigurationAllowed, voicePackReady} from "./practiceSimulatorVoiceConfig";

export const VOICE_API = "/api/practice-simulator/practice/sessions";
export const voiceBody = (response) => response?.data?.data || response?.data || {};
export const voiceCode = (error) => {const data = voiceBody(error?.response); return data.error_code || data.error || data.code || data.reason;};
const messages = {
  public_session_required: "Your session expired. Sign in again to continue.",
  pack_not_found: "This practice pack is no longer available.",
  study_pack_not_ready: "This practice pack is not ready for voice practice.",
  assessment_simulation_unavailable: "Assessment simulation is not available yet.",
  practice_session_not_found: "This practice session is no longer available.",
  practice_session_conflict: "This practice session changed. Reconnect to restore its current progress.",
  invalid_question_order: "The question changed. Reconnect to restore the current question.",
  practice_session_completed: "This practice session is complete.",
  practice_incomplete: "There are still questions to complete. Reconnect to continue this session.",
  invalid_practice_request: "The practice request could not be accepted. Please try again.",
  invalid_tool_call: "The answer could not be saved. Voice practice will try to correct the request.",
  invalid_question_result: "The answer result could not be saved. Voice practice will try to correct it.",
  invalid_evaluator_output: "The answer could not be evaluated. Reconnect to try again.",
  insufficient_credits: "You do not have enough AI credits to start this practice session.",
  credit_service_unavailable: "Credit information is temporarily unavailable. Please try again.",
  realtime_unavailable: "Voice connection is unavailable. Reconnect to continue this same session.",
  realtime_rate_limited: "Too many voice connection attempts were made for this session. Please wait before reopening this same practice session. You will not be charged again.",
  practice_simulator_unavailable: "The practice simulator is temporarily unavailable.",
  invalid_practice_report: "Your practice was completed, but its report is temporarily unavailable.",
};
export const voiceMessage = (error) => messages[voiceCode(error)] || "Voice practice was interrupted. Try again to continue without starting another session.";
const publicFields = ["session_id", "status", "pack_id", "pack_mode", "simulation_type", "assessor_role", "pack_title", "pack_revision", "question_count", "questions_completed", "current_question_id", "follow_up_used", "required_credits", "pause_duration_mode"];
export const publicVoiceState = (data) => {
  const state = Object.fromEntries(publicFields.filter((key) => Object.hasOwn(data, key)).map((key) => [key, data[key]]));
  const report = validatedPracticeReport(data);
  return report ? {...state, report} : state;
};
const invalidTool = () => Object.assign(new Error("Invalid tool"), {response: {status: 422, data: {error: "invalid_tool_call"}}});
const api = {...PUBLIC_SESSION_CONFIG, headers: {"Content-Type": "application/json"}};
async function post(url, payload) {
  const response = await axios.post(url, payload, api), data = voiceBody(response);
  if (data.success === false) throw Object.assign(new Error("Practice request failed"), {response});
  return data;
}

// Simulator lifecycle only. Audio, SDK configuration and playback stay in the proven transport.
export class SimulatorVoiceSession {
  constructor({onState, onPhase, onError, onExpired, onPersist, assessmentEnabled, onStartupError = () => {}}) {
    Object.assign(this, {onState, onPhase, onError, onExpired, onPersist, assessmentEnabled, onStartupError});
    this.results = new Map(); this.pending = new Set(); this.generation = 0; this.failures = 0;
    this.state = null; this.phase = "setup"; this.busy = false; this.muted = false;
  }
  move(phase) {
    this.phase = this.rateLimited && phase === "disconnected" ? "rate_limited" : phase;
    this.onPhase(this.phase);
    if (this.phase === "rate_limited") this.onError(messages.realtime_rate_limited);
  }
  accept(data) {
    if (this.disposed) return;
    if (!safeVoiceId(data.session_id) || (this.sessionId && data.session_id !== this.sessionId) || data.pack_id !== this.packId || (data.status !== "completed" && !voiceConfigurationAllowed(data, this.assessmentEnabled))) throw invalidTool();
    this.state = publicVoiceState(data); this.onState(this.state); this.onPersist(data.session_id);
    if (data.status === "completed") {this.completionSaved = true; this.stop(); this.move("completed");}
  }
  gate(enabled) {
    this.listening = enabled;
    this.stream?.getAudioTracks().forEach((track) => {track.enabled = enabled && !this.muted;});
    this.transport?.mute(!enabled || this.muted);
  }
  stop() {
    this.startupAttempt?.cancel(); this.startupAttempt = null; this.onStartupError(null);
    this.generation += 1; clearTimeout(this.timer); this.gate(false);
    const active = this.transport; this.transport = null; active?.close();
    this.stream?.getTracks().forEach((track) => track.stop()); this.stream = null;
    this.pending.clear(); this.speaking = false;
  }
  dispose() {this.disposed = true; this.stop(); this.results.clear();}
  async failure(error) {
    if (this.disposed || error?.code === "startup_cancelled") return;
    if (error?.stage) this.startupStage = "failed";
    const startupError = error?.stage ? {stage: error.stage, code: error.code || error.name} : null;
    error = error?.cause || error;
    this.stop();
    if (isPublicSessionError(error)) {
      this.state = null; this.results.clear(); this.onState(null); this.onExpired(); return;
    }
    if (voiceCode(error) === "realtime_rate_limited") {this.rateLimited = true; this.move("rate_limited"); return;}
    if (voiceCode(error) === "invalid_practice_report") {this.move("report_unavailable"); this.onError(voiceMessage(error)); return;}
    if (voiceCode(error) === "practice_session_completed" && this.sessionId) {
      this.completionSaved = true; await this.loadReport(); return;
    }
    this.move(["pack_not_found", "practice_session_not_found", "assessment_simulation_unavailable"].includes(voiceCode(error)) ? "stopped" : "disconnected");
    if (startupError && this.phase === "disconnected") {this.onStartupError(startupError); this.onError("");}
    else this.onError(voiceMessage(error));
  }
  beginStartup() {
    this.startupAttempt?.cancel(); this.onStartupError(null);
    this.startupAttempt = createStartupAttempt((stage) => {this.startupStage = stage;});
    return this.startupAttempt;
  }
  async refresh(attempt) {
    const generation = this.generation;
    const request = () => axios.get(`${VOICE_API}/${encodeURIComponent(this.sessionId)}`, PUBLIC_SESSION_CONFIG);
    const data = voiceBody(await (attempt ? attempt.wait("loading_configuration", request, CONNECTION_TIMEOUT_MS) : request()));
    if (generation === this.generation) this.accept(data);
    return data;
  }
  async loadReport() {
    if (this.reportLoading || this.disposed) return;
    this.reportLoading = true; this.stop(); this.move("loading_report"); this.onError("");
    const generation = this.generation;
    try {
      const data = await this.refresh();
      if (!this.disposed && generation === this.generation && data.status !== "completed") {
        this.move(this.completionSaved ? "report_unavailable" : "disconnected");
        if (this.completionSaved) this.onError(REPORT_UNAVAILABLE);
      }
    } catch (error) {
      if (this.disposed || generation !== this.generation) return;
      if (isPublicSessionError(error)) await this.failure(error);
      else {this.move("report_unavailable"); this.onError(voiceCode(error) ? voiceMessage(error) : REPORT_UNAVAILABLE);}
    } finally {this.reportLoading = false;}
  }
  async recover(sessionId, packId) {
    if (this.busy || this.disposed) return;
    if (this.completionSaved) {await this.loadReport(); return;}
    if (this.rateLimited) return;
    this.busy = true; this.sessionId = sessionId; this.packId = packId; this.stop(); this.move("connecting"); this.onError("");
    const generation = this.generation, attempt = this.beginStartup();
    try {
      const data = await this.refresh(attempt);
      if (this.disposed || generation !== this.generation) return;
      if (data.status === "completed") return;
      if (!["active", "created"].includes(data.status)) throw invalidTool();
      this.failures = 0;
      const connection = await attempt.wait("requesting_token", () => post(`${VOICE_API}/${encodeURIComponent(sessionId)}/connection`, {}), CONNECTION_TIMEOUT_MS);
      if (generation !== this.generation) return;
      await this.connect(connection.realtime || connection);
    } catch (error) {await this.failure(error);} finally {this.busy = false;}
  }
  async start({pack_id, idempotency_key, pause_duration_mode}, onCreated) {
    if (this.busy || this.sessionId || this.disposed || this.rateLimited) return;
    this.busy = true; this.packId = pack_id; this.move("creating"); this.onError("");
    const generation = this.generation;
    try {
      const data = await post(VOICE_API, {pack_id, idempotency_key, pause_duration_mode});
      if (this.disposed || generation !== this.generation) return;
      this.sessionId = data.session_id; this.accept(data); await onCreated(data);
      if (data.status !== "completed" && generation === this.generation) await this.connect(data.realtime);
    } catch (error) {
      const data = voiceBody(error?.response);
      if (data.reconnectable === true && safeVoiceId(data.session_id)) {this.sessionId = data.session_id; this.onPersist(data.session_id);}
      if (this.sessionId || isPublicSessionError(error) || voiceCode(error) === "realtime_rate_limited") await this.failure(error);
      else {this.move("setup"); this.onError(voiceMessage(error));}
    } finally {this.busy = false;}
  }
  async connect(realtime) {
    if (this.disposed || this.rateLimited) return;
    if (!realtime?.client_secret || !realtime?.model) throw new Error("Missing connection");
    this.stop(); const generation = this.generation, attempt = this.beginStartup();
    try {
      this.move("requesting_microphone");
      const stream = await attempt.wait("opening_microphone", () => navigator.mediaDevices.getUserMedia({audio: {echoCancellation: true, noiseSuppression: true, autoGainControl: true}}), MICROPHONE_TIMEOUT_MS, (late) => late.getTracks().forEach((track) => track.stop()));
      if (generation !== this.generation) {stream.getTracks().forEach((track) => track.stop()); return;}
      this.stream = stream; this.gate(false);
      const active = createStudyBuddyRealtimeTransport(stream); this.transport = active; active.mute(true);
      const current = () => this.transport === active && generation === this.generation;
      active.onDisconnected(() => {if (current()) this.failure({response: {data: {error: "realtime_unavailable"}}});});
      active.onEvent((event) => {
        if (!current()) return;
        if (["response.created", "response.audio.delta", "response.output_audio.delta", "input_audio_buffer.committed"].includes(event?.type)) {this.gate(false); if (!this.speaking) this.move("processing");}
      });
      active.onAssistantAudioStart(() => {if (current()) {clearTimeout(this.timer); this.speaking = true; this.gate(false); this.move("speaking");}});
      active.onAssistantAudioStopped(() => {if (current() && this.speaking) {this.speaking = false; this.gate(false); this.move("ready");}});
      active.onFunctionCall((call) => {if (current()) this.tool(call, active, generation);});
      this.move("connecting");
      await attempt.wait("connecting_realtime", () => active.connect({clientSecret: realtime?.client_secret, model: realtime?.model}), CONNECTION_TIMEOUT_MS, () => active.close());
      this.startupStage = "ready";
      if (!current()) {active.close(); return;}
      this.gate(false); this.move("preparing"); active.requestResponse();
      this.timer = setTimeout(() => {if (current()) this.failure({response: {data: {error: "realtime_unavailable"}}});}, 15000);
    } catch (error) { if (error.code !== "startup_cancelled") throw error; }
  }
  ready() {if (this.phase !== "ready" || this.speaking) return; this.muted = false; this.gate(true); this.move("listening");}
  toggleMute() {if (this.phase !== "listening") return; this.muted = !this.muted; this.gate(true);}
  toolPayload(call) {
    if (!call.callId || typeof call.callId !== "string") throw invalidTool();
    let args; try {args = JSON.parse(call.arguments || "{}");} catch (_) {throw invalidTool();}
    if (!args || typeof args !== "object" || Array.isArray(args)) throw invalidTool();
    const state = this.state, assessment = state?.pack_mode === "assessment_simulation";
    if (assessment) {
      if (!["evaluate_practice_simulator_answer", "complete_practice_simulator_session"].includes(call.name) || args.session_id !== state.session_id || args.pack_id !== state.pack_id) throw invalidTool();
      const fields = ["session_id", "pack_id", "call_id", ...(call.name === "evaluate_practice_simulator_answer" ? ["question_id", "transcript"] : [])];
      if (Object.keys(args).some((field) => !fields.includes(field)) || (Object.hasOwn(args, "call_id") && (typeof args.call_id !== "string" || !args.call_id.trim()))) throw invalidTool();
      const payload = {tool_name: call.name, session_id: state.session_id, pack_id: state.pack_id, call_id: call.callId};
      if (call.name === "complete_practice_simulator_session") return {endpoint: "complete", payload};
      if (!state.current_question_id || args.question_id !== state.current_question_id || typeof args.transcript !== "string" || !args.transcript.trim()) throw invalidTool();
      return {endpoint: "answers/evaluate", payload: {...payload, question_id: state.current_question_id, transcript: args.transcript}};
    }
    if (call.name === "complete_study_buddy_practice") return {endpoint: "complete", payload: {}};
    if (call.name !== "finalize_study_question" || !Number.isInteger(args.question_position) || args.question_position !== state?.questions_completed || !["correct", "partial", "missed"].includes(args.result) || ![args.covered_points, args.missed_points].every((list) => Array.isArray(list) && list.every((index) => Number.isInteger(index) && index >= 0))) throw invalidTool();
    return {endpoint: "questions/finalize", payload: {question_position: args.question_position, covered_points: args.covered_points, missed_points: args.missed_points, result: args.result}};
  }
  async tool(call, active, generation) {
    const current = () => this.transport === active && generation === this.generation;
    const send = (result, resume) => {if (current()) active.sendFunctionCallOutput({callId: call.callId, output: JSON.stringify(result), continueResponse: resume});};
    if (!call.callId || this.pending.has(call.callId)) return;
    // Re-delivery must not ask the backend or trigger another follow-up/response.
    if (this.results.has(call.callId)) {send(this.results.get(call.callId), false); return;}
    if (this.pending.size) {send({ok: false, advance: false, retryable: false, error: "practice_session_conflict"}, false); await this.failure({response: {status: 409, data: {error: "practice_session_conflict"}}}); return;}
    this.pending.add(call.callId); this.gate(false);
    try {
      const {endpoint, payload} = this.toolPayload(call);
      const data = await post(`${VOICE_API}/${encodeURIComponent(this.sessionId)}/${endpoint}`, payload);
      if (!current()) return;
      // Realtime receives acknowledgement only; completed report content comes from GET to browser state.
      if (endpoint === "complete") {
        const result = {ok: true, completed: true};
        this.results.set(call.callId, result); send(result, false);
        this.completionSaved = true;
        await this.loadReport();
        return;
      }
      const material = endpoint === "questions/finalize";
      const result = material ? {ok: true, next_question_position: data.next_question_position, ...(data.advance === false ? {advance: false} : {})} : data;
      this.results.set(call.callId, result);
      await this.refresh();
      if (!current()) return;
      this.failures = 0; this.onError("");
      const materialDone = material && data.advance !== false && this.state.questions_completed >= this.state.question_count;
      send(result, !materialDone);
      if (materialDone) {await post(`${VOICE_API}/${encodeURIComponent(this.sessionId)}/complete`, {}); if (current()) {this.completionSaved = true; await this.loadReport();}}
    } catch (error) {
      if (!current()) return;
      // A failed progress refresh must not replace a successfully saved backend result.
      if (this.results.has(call.callId)) {send(this.results.get(call.callId), false); await this.failure(error); return;}
      const failure = classifyStudyBuddyFinalizationError(error);
      if (!isPublicSessionError(error) && failure.kind === "deterministic" && !["practice_session_completed", "invalid_practice_report"].includes(voiceCode(error))) {
        this.failures += 1;
        const retry = this.failures < MAX_DETERMINISTIC_FINALIZATION_FAILURES;
        const result = {...correctiveFinalizationResult(failure.safeCode), retryable: retry, ...(!retry ? {instruction: "Stop. The student must reconnect before continuing this same practice session."} : {})};
        this.results.set(call.callId, result); send(result, retry);
        this.onError(retry ? voiceMessage(error) : "The answer could not be saved after several attempts. Reconnect to this same session without another charge.");
        this.gate(false); this.move("processing");
        if (!retry) {this.stop(); this.move("disconnected");}
      } else {
        const result = {ok: false, advance: false, retryable: false, error: "practice_request_failed"};
        this.results.set(call.callId, result); send(result, false); await this.failure(error);
      }
    } finally {this.pending.delete(call.callId);}
  }
}
